import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../../config";
import { MockStatsService, API_BASE_URL } from "../../../services/MockStatsService";

const DetailResourceCompOpenData = ({ formatDate }) => {
    const { id, id_resource } = useParams();
    const [dataDetail, setDataDetail] = useState(null);
    const [dataDetail2, setDataDetail2] = useState([]);
    const [specificResource, setSpecificResource] = useState(null);
    const [loading, setLoading] = useState(true);
    const [excelData, setExcelData] = useState([]);
    const [excelHeaders, setExcelHeaders] = useState([]);
    const [pdfUrl, setPdfUrl] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(global.config.host + `/api/3/action/package_show?id=${id}`);
                setDataDetail(response.data.result);
                setDataDetail2(response.data.result.resources);
            } catch (error) {
                console.error('Error fetching data detail', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    useEffect(() => {
        if (dataDetail2.length > 0 && id_resource) {
            const resource = dataDetail2.find(resource => resource.id === id_resource);
            setSpecificResource(resource);
        }
    }, [dataDetail2, id_resource]);

    useEffect(() => {
        const fetchExcelData = async () => {
            if (specificResource && specificResource.format.toLowerCase() === "xlsx") {
                try {
                    const response = await axios.get(specificResource.url, {
                        responseType: "arraybuffer",
                    });

                    const workbook = XLSX.read(new Uint8Array(response.data), { type: "array" });
                    const sheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[sheetName];
                    
                    // Deteksi range data yang sebenarnya
                    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
                    
                    // Parse seluruh sheet dengan pertahankan semua data
                    const rawData = [];
                    for (let row = range.s.r; row <= range.e.r; row++) {
                        const rowData = [];
                        for (let col = range.s.c; col <= range.e.c; col++) {
                            const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
                            const cell = sheet[cellRef];
                            
                            let cellVal = '';
                            if (cell) {
                                // Extract Excel's own formatted text if it's a percentage
                                if (cell.w && String(cell.w).includes('%')) {
                                    cellVal = cell.w;
                                } else {
                                    cellVal = cell.v;
                                }
                            }
                            rowData.push(cellVal);
                        }
                        rawData.push(rowData);
                    }
                    
                    // Auto-detect number of header rows
                    // Rule: Skip rows yang berisi sub-header pattern (nomor urut dalam kurung seperti "(1)", "(2)", dll)
                    let numHeaderRows = 1; // Default: 1 header row
                    
                    if (rawData.length > 1) {
                        const row2 = rawData[1] || [];
                        // Cek jika row 2 pattern "(1)", "(2)", etc - maka ada 2 header rows
                        const isSubHeaderRow = row2.some((cell) => {
                            const cellStr = String(cell).trim();
                            return /^\(\d+\)$/.test(cellStr);
                        });
                        
                        if (isSubHeaderRow) {
                            numHeaderRows = 2; // 2 header rows, data mulai row 3
                        }
                    }
                    
                    // Konversi array of arrays menjadi object dengan header dari baris pertama
                    const headers = rawData[0] || [];
                    // Data dimulai dari row setelah header rows
                    const jsonData = rawData.slice(numHeaderRows).map((row) => {
                        const obj = {};
                        headers.forEach((header, index) => {
                            obj[header] = row[index] !== undefined ? row[index] : '';
                        });
                        return obj;
                    });
                    
                    setExcelHeaders(headers); // Store headers with preserved order
                    setExcelData(jsonData);
                } catch (error) {
                    console.error("Error fetching Excel data", error);
                }
            }
        };

        fetchExcelData();
    }, [specificResource]);

    useEffect(() => {
        let objectUrl = null;
        
        const fetchPdfData = async () => {
            if (specificResource && specificResource.format.toLowerCase() === "pdf") {
                try {
                    const response = await axios.get(specificResource.url, {
                        responseType: "blob",
                    });
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    objectUrl = URL.createObjectURL(blob);
                    setPdfUrl(objectUrl);
                } catch (error) {
                    console.error("Error fetching PDF data", error);
                    setPdfUrl(specificResource.url);
                }
            }
        };

        fetchPdfData();

        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [specificResource]);

    const bytesToMB = (bytes) => {
        return (bytes / (1024 * 1024)).toFixed(2); // 1 MB = 1024 KB = 1024 * 1024 bytes
    };

    const generatePDF = () => {
        if (!excelData || excelData.length === 0) return;
        
        const doc = new jsPDF('landscape');
        
        doc.setFontSize(14);
        doc.text(specificResource.name || 'Dataset', 14, 15);
        
        const bodyData = excelData.map(row => {
            return excelHeaders.map(header => {
                const cellValue = row[header];
                return formatCellValue(cellValue, header);
            });
        });
        
        autoTable(doc, {
            head: [excelHeaders],
            body: bodyData,
            startY: 20,
            theme: 'grid',
            styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
            headStyles: { fillColor: [41, 128, 185], textColor: 255 }
        });
        
        doc.save(`${specificResource.name || 'dataset'}.pdf`);
        MockStatsService.incrementDatasetStat(id, 'download');
    };

    // Helper: Format cell value - detect dan format percentage
    const formatCellValue = (value, columnHeader = '') => {
        if (value === '' || value === null || value === undefined) return '-';
        
        // Jika adalah number
        if (typeof value === 'number') {

            
            // Format number dengan thousand separator jika > 999
            if (value > 999 || value < -999) {
                return new Intl.NumberFormat('id-ID').format(value);
            }
        }
        
        // Jika adalah string, return as-is
        return String(value).trim();
    };

    return (
        <section className="d-flex align-items-center" style={{ marginTop: '60px' }}>
            <div className="container" data-aos="fade-in">
                <div className="row content" data-aos="fade-in">
                    <Link to={`/open_data/kumpulan_data/${id}`} className="breadcrumb"><i className="bx bx-arrow-back"></i> Kembali</Link>
                </div>
                {
                    loading ? (
                        <div className="col-md-12 text-center" data-aos="fade-in">
                            <h4>Loading Data...</h4>
                        </div>
                    ) :
                        (
                            !specificResource ? (
                                <div className="col-lg-12 box-no-data"> No Data Found</div>
                            ) : (

                                <div className="row content" >
                                    <div className="col-md-12 m-b-20">
                                        <p className="author">
                                            <i className="bx bx-time"></i> {specificResource.last_modified ? formatDate(specificResource.last_modified) : '-'}
                                        </p>
                                        <h4 className="m-b-10 font-weight-600" >
                                            {specificResource.name ? specificResource.name : '-'}
                                            
                                            {specificResource.format.toLowerCase() === 'xlsx' || specificResource.format.toLowerCase() === 'xls' ? (
                                                <>
                                                    <a href={specificResource.url} target="_blank" title={specificResource.url} className="btn-default m-l-10" onClick={() => MockStatsService.incrementDatasetStat(id, 'download')}>
                                                        <i className="bx bx-download"></i> Download Excel
                                                    </a>
                                                    <button onClick={generatePDF} className="btn-default m-l-10" style={{border: 'none', cursor: 'pointer'}}>
                                                        <i className="bx bxs-file-pdf"></i> Download PDF
                                                    </button>
                                                </>
                                            ) : specificResource.format.toLowerCase() === 'pdf' ? (
                                                <>
                                                    <a href={specificResource.url} target="_blank" title={specificResource.url} className="btn-default m-l-10" onClick={() => MockStatsService.incrementDatasetStat(id, 'download')}>
                                                        <i className="bx bx-download"></i> Download PDF
                                                    </a>
                                                    {(() => {
                                                        const excelSibling = dataDetail2.find(r => r.format.toLowerCase() === 'xlsx' || r.format.toLowerCase() === 'xls');
                                                        if (excelSibling) {
                                                            return (
                                                                <a href={excelSibling.url} target="_blank" title={excelSibling.url} className="btn-default m-l-10" onClick={() => MockStatsService.incrementDatasetStat(id, 'download')}>
                                                                    <i className="bx bx-file-blank"></i> Format Excel (Asli)
                                                                </a>
                                                            );
                                                        } else {
                                                            const convertUrl = `${API_BASE_URL}/convert/pdf-to-excel?url=${encodeURIComponent(specificResource.url)}`;
                                                            return (
                                                                <a href={convertUrl} target="_blank" title="Convert to Excel" className="btn-default m-l-10" onClick={() => MockStatsService.incrementDatasetStat(id, 'download')}>
                                                                    <i className="bx bx-file-blank"></i> Format Excel (Otomatis)
                                                                </a>
                                                            );
                                                        }
                                                    })()}
                                                </>
                                            ) : (
                                                <a href={specificResource.url} target="_blank" title={specificResource.url} className="btn-default m-l-10" onClick={() => MockStatsService.incrementDatasetStat(id, 'download')}>
                                                    <i className="bx bx-download"></i> Download
                                                </a>
                                            )}
                                        </h4>
                                    </div>
                                    <div className="col-md-12">
                                        <div className="box-additional">
                                            <div className="header-additional">
                                                <i className="bx bx-data"></i> Data and Resources Review
                                            </div>
                                            <div className="p-10 box-additional-body">
                                                {/* Tampilkan Excel Data jika format xlsx */}
                                                {specificResource.format.toLowerCase() === "xlsx" && excelData.length > 0 && (
                                                    <div className="table-responsive">
                                                        <table className="table table-striped table-bordered table-sm">
                                                            <thead className="thead-dark">
                                                                <tr>
                                                                    {excelHeaders.map((header, index) => (
                                                                        <th key={index} style={{whiteSpace: 'nowrap'}}>{header}</th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {excelData.map((row, rowIndex) => (
                                                                    <tr key={rowIndex}>
                                                                        {excelHeaders.map((header, cellIndex) => {
                                                                            const cellValue = row[header];
                                                                            // Detect jika kolom ini number atau percentage (untuk alignment)
                                                                            const isNumeric = typeof cellValue === 'number' || String(cellValue).match(/^[\d,%.]+$/);
                                                                            return (
                                                                                <td 
                                                                                    key={cellIndex}
                                                                                    style={{textAlign: isNumeric ? 'center' : 'left', whiteSpace: 'normal'}}
                                                                                >
                                                                                    {formatCellValue(cellValue, header)}
                                                                                </td>
                                                                            );
                                                                        })}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                                {/* Tampilkan PDF jika format pdf */}
                                                {specificResource.format.toLowerCase() === "pdf" && pdfUrl && (
                                                    <div className="pdf-viewer mt-3">
                                                        <object
                                                            data={pdfUrl}
                                                            type="application/pdf"
                                                            width="100%"
                                                            height="600px"
                                                        >
                                                            <iframe
                                                                src={pdfUrl}
                                                                title="PDF Preview"
                                                                width="100%"
                                                                height="600px"
                                                            >
                                                                This browser does not support PDFs.
                                                            </iframe>
                                                        </object>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="box-additional">
                                            <div className="header-additional">
                                                <i className="bx bx-info-circle"></i> Additional Information
                                            </div>
                                            <div>
                                                <table className="table table-striped-columns m-b-0">
                                                    <tbody>
                                                        <tr>
                                                            <td className="font-weight-100">Created</td>
                                                            <td>{
                                                                specificResource.created != null ? formatDate(specificResource.created) : '-'
                                                            }</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-100">Data last updated</td>
                                                            <td>{
                                                                specificResource.last_modified != null ? formatDate(specificResource.last_modified) : '-'
                                                            }</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-100">Metadata last updated</td>
                                                            <td>{
                                                                specificResource.metadata_modified != null ? formatDate(specificResource.metadata_modified) : '-'
                                                            }</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-100">Format</td>
                                                            <td>{
                                                                specificResource.format != null ? specificResource.format : '-'
                                                            }</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-100">License</td>
                                                            <td>{
                                                                (dataDetail.license_id != null) ?
                                                                    (
                                                                        <a href={dataDetail.license_url} target="_blank">
                                                                            {dataDetail.license_title}
                                                                        </a>

                                                                    ) : '-'
                                                            }</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-100">Mimetype</td>
                                                            <td>{
                                                                specificResource.mimetype != null ? specificResource.mimetype : '-'
                                                            }</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-100">On same domain</td>
                                                            <td>{
                                                                dataDetail.isopen != null ? (
                                                                    dataDetail.isopen === true ? <label className="label bg-green font-weight-700">True</label> : <label className="label bg-danger font-weight-700">False</label>
                                                                ) : '-'

                                                            }</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-100">Position</td>
                                                            <td>{
                                                                specificResource.position != null ? specificResource.position : '-'
                                                            }</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-100">Size</td>
                                                            <td>{
                                                                specificResource.size != null ? `${bytesToMB(specificResource.size)} MB` : '-'
                                                            }</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-100">State</td>
                                                            <td>{
                                                                specificResource.state != null ? (
                                                                    specificResource.state === 'active' ? <label className="label bg-green font-weight-700">{specificResource.state}</label> : <label className="label bg-danger font-weight-700">{specificResource.state}</label>
                                                                ) : '-'
                                                            }</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-100">Url type</td>
                                                            <td>{
                                                                specificResource.url_type != null ? specificResource.url_type : '-'
                                                            }</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        )
                }
            </div>
        </section>
    )
}

export default DetailResourceCompOpenData;